import { useRouter } from "expo-router";
import { LinearGradient } from "expo-linear-gradient";
import {
  Clock3,
  Crown,
  Leaf,
  MapPin,
  Menu,
  Phone,
  ShieldCheck,
  Sparkles,
  Users,
} from "../../../components/lucide-icons";
import { ImageBackground, Pressable, ScrollView, StyleSheet, Text, View, useWindowDimensions } from "react-native";

import { AppShell } from "../../../components/layout";
import { semantic, spacing } from "../../../theme/tokens";
import { arrangementRoutes } from "../routes";

const monarchHeroImage = require("../../../../assets/monarch-arrangement-hero.png");

export default function ArrangementStartScreen() {
  const router = useRouter();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 760;

  const website = (
    <View style={[styles.siteSurface, isDesktop ? styles.siteSurfaceDesktop : null]}>
      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
        <ImageBackground
          source={monarchHeroImage}
          resizeMode="cover"
          style={[styles.hero, isDesktop ? styles.heroDesktop : null]}
          imageStyle={styles.heroImage}
        >
          <LinearGradient colors={[semantic.overlay.inverseStrong, semantic.text.inverseMuted, semantic.overlay.inverseSubtle]} style={styles.heroWash}>
            <View style={styles.heroTop}>
              <View style={styles.logoBlock}>
                <Crown color={semantic.accent.foreground} size={40} strokeWidth={1.5} />
                <Text style={styles.logoName}>MONARCH</Text>
                <Text style={styles.logoSubline}>FUNERAL HOME</Text>
              </View>
              <Pressable accessibilityRole="button" accessibilityLabel="Open menu" style={styles.menuButton}>
                <Menu color={semantic.action.primary} size={28} strokeWidth={2} />
              </Pressable>
            </View>
            <View style={styles.heroCopy}>
              <Text style={styles.heroTitle}>Plan a meaningful service with Monarch</Text>
              <Text style={styles.heroBody}>Compassionate guidance. Personalized care. Here when you need us most.</Text>
            </View>
          </LinearGradient>
        </ImageBackground>

        <View style={styles.actionGrid}>
          <ActionCard
            title="Start Arrangements Online"
            body="Guide your family through planning with care and clarity."
            buttonLabel="Get Started"
            primary
            icon={Users}
            onPress={() => router.push("/demo/monarch/arrangements/start" as never)}
          />
          <ActionCard
            title="Pre-Plan for the Future"
            body="Ease the burden on your loved ones with a plan in place."
            buttonLabel="Learn More"
            icon={Leaf}
            onPress={() => router.push(arrangementRoutes.studio as never)}
          />
          <ActionCard
            title="Continue Existing Arrangement"
            body="Return with your secure family link or a staff-provided access code."
            buttonLabel="Resume"
            icon={ShieldCheck}
            onPress={() => router.push("/family/portal/monarch-family" as never)}
          />
        </View>

        <View style={styles.contactStrip}>
          <ContactItem icon={Phone} text="(716) 692-4555" />
          <ContactItem icon={MapPin} text="3080 Delaware Ave Kenmore, NY 14217" />
          <ContactItem icon={Clock3} text="Available 24/7" />
        </View>

        <View style={styles.trustGrid}>
          <TrustItem icon={Users} title="Family Focused" body="Personalized attention at every step." />
          <TrustItem icon={ShieldCheck} title="Local & Trusted" body="Proudly serving Western New York families." />
          <TrustItem icon={Sparkles} title="Thoughtful Details" body="Honoring every life with dignity and care." />
        </View>

        <View style={styles.footer}>
          <View style={styles.footerLogo}>
            <Crown color={semantic.accent.foreground} size={28} strokeWidth={1.5} />
            <View>
              <Text style={styles.footerLogoText}>MONARCH</Text>
              <Text style={styles.footerLogoSubline}>FUNERAL HOME</Text>
            </View>
          </View>
          <Text style={styles.powered}>Powered by WeHonor</Text>
        </View>
      </ScrollView>
    </View>
  );

  return (
    <AppShell role="public" accessibilityLabel="Monarch arrangement website">
      <LinearGradient colors={[semantic.surface.canvas, semantic.surface.canvasAlt]} style={styles.stage}>
        {isDesktop ? <View style={styles.desktopMat}>{website}</View> : website}
      </LinearGradient>
    </AppShell>
  );
}

function ActionCard({
  title,
  body,
  buttonLabel,
  icon: Icon,
  primary,
  onPress,
}: {
  title: string;
  body: string;
  buttonLabel: string;
  icon: typeof Users;
  primary?: boolean;
  onPress: () => void;
}) {
  return (
    <View style={[styles.actionCard, primary ? styles.actionCardPrimary : styles.actionCardSecondary]}>
      <Icon color={primary ? semantic.action.primary : semantic.accent.foreground} size={38} strokeWidth={1.6} />
      <Text style={styles.actionTitle}>{title}</Text>
      <Text style={styles.actionBody}>{body}</Text>
      <Pressable
        accessibilityRole="button"
        onPress={onPress}
        style={[styles.actionButton, primary ? styles.actionButtonPrimary : styles.actionButtonSecondary]}
      >
        <Text style={[styles.actionButtonText, primary ? styles.actionButtonTextPrimary : styles.actionButtonTextSecondary]}>
          {buttonLabel}
        </Text>
      </Pressable>
    </View>
  );
}

function ContactItem({ icon: Icon, text }: { icon: typeof Phone; text: string }) {
  return (
    <View style={styles.contactItem}>
      <Icon color={semantic.text.inverse} size={22} strokeWidth={1.8} />
      <Text style={styles.contactText}>{text}</Text>
    </View>
  );
}

function TrustItem({
  icon: Icon,
  title,
  body,
}: {
  icon: typeof Users;
  title: string;
  body: string;
}) {
  return (
    <View style={styles.trustItem}>
      <View style={styles.trustIcon}>
        <Icon color={semantic.text.secondary} size={27} strokeWidth={1.6} />
      </View>
      <View style={styles.trustCopy}>
        <Text style={styles.trustTitle}>{title}</Text>
        <Text style={styles.trustBody}>{body}</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: semantic.surface.canvas,
  },
  stage: {
    flex: 1,
  },
  desktopMat: {
    flex: 1,
    alignItems: "stretch",
    justifyContent: "flex-start",
    padding: spacing.lg,
  },
  siteSurface: {
    flex: 1,
    width: "100%",
    backgroundColor: semantic.surface.raised,
    overflow: "hidden",
  },
  siteSurfaceDesktop: {
    maxWidth: 1280,
    alignSelf: "center",
    borderRadius: 20,
    borderWidth: 1,
    borderColor: semantic.overlay.darkSubtle,
    shadowColor: semantic.text.muted,
    shadowOpacity: 0.18,
    shadowRadius: 30,
    shadowOffset: { width: 0, height: 18 },
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    backgroundColor: semantic.surface.raised,
  },
  hero: {
    minHeight: 360,
    justifyContent: "flex-start",
  },
  heroDesktop: {
    minHeight: 520,
  },
  heroImage: {
    opacity: 0.92,
  },
  heroWash: {
    flex: 1,
    padding: 26,
    justifyContent: "space-between",
  },
  heroTop: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  logoBlock: {
    gap: 2,
  },
  logoName: {
    color: semantic.action.primary,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 36,
    lineHeight: 38,
    letterSpacing: 2,
  },
  logoSubline: {
    color: semantic.action.primary,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 13,
    lineHeight: 17,
    letterSpacing: 5,
  },
  menuButton: {
    width: 40,
    height: 40,
    alignItems: "center",
    justifyContent: "center",
  },
  heroCopy: {
    paddingTop: 42,
    paddingBottom: 18,
    gap: 12,
  },
  heroTitle: {
    color: semantic.action.primary,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 43,
    lineHeight: 49,
  },
  heroBody: {
    color: semantic.text.primary,
    fontFamily: "Manrope_500Medium",
    fontSize: 15,
    lineHeight: 22,
    maxWidth: 310,
  },
  actionGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
    padding: 16,
    backgroundColor: semantic.surface.raised,
  },
  actionCard: {
    flex: 1,
    minWidth: 250,
    minHeight: 172,
    borderRadius: 8,
    borderWidth: 1.5,
    backgroundColor: semantic.overlay.inverseStrong,
    alignItems: "center",
    padding: 14,
    gap: 8,
  },
  actionCardPrimary: {
    borderColor: semantic.action.primary,
  },
  actionCardSecondary: {
    borderColor: semantic.accent.foreground,
  },
  actionTitle: {
    color: semantic.text.primary,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 20,
    lineHeight: 23,
    textAlign: "center",
  },
  actionBody: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 11,
    lineHeight: 16,
    textAlign: "center",
  },
  actionButton: {
    marginTop: "auto",
    width: "100%",
    borderRadius: 6,
    borderWidth: 1.5,
    alignItems: "center",
    paddingVertical: 8,
  },
  actionButtonPrimary: {
    backgroundColor: semantic.action.primary,
    borderColor: semantic.action.primary,
  },
  actionButtonSecondary: {
    backgroundColor: semantic.surface.raised,
    borderColor: semantic.accent.foreground,
  },
  actionButtonText: {
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 12,
    lineHeight: 16,
  },
  actionButtonTextPrimary: {
    color: semantic.text.inverse,
  },
  actionButtonTextSecondary: {
    color: semantic.accent.foreground,
  },
  contactStrip: {
    backgroundColor: semantic.action.primary,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 15,
  },
  contactItem: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
  },
  contactText: {
    color: semantic.text.inverse,
    fontFamily: "Manrope_700Bold",
    fontSize: 10,
    lineHeight: 14,
  },
  trustGrid: {
    flexDirection: "row",
    gap: 12,
    backgroundColor: semantic.surface.raised,
    paddingHorizontal: 22,
    paddingVertical: 26,
  },
  trustItem: {
    flex: 1,
    alignItems: "center",
    gap: 8,
  },
  trustIcon: {
    width: 50,
    height: 50,
    borderRadius: 25,
    borderWidth: 1,
    borderColor: semantic.border.muted,
    alignItems: "center",
    justifyContent: "center",
  },
  trustCopy: {
    gap: 4,
  },
  trustTitle: {
    color: semantic.text.primary,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 16,
    lineHeight: 18,
    textAlign: "center",
  },
  trustBody: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 10,
    lineHeight: 14,
    textAlign: "center",
  },
  footer: {
    minHeight: 94,
    backgroundColor: semantic.surface.muted,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 28,
  },
  footerLogo: {
    flexDirection: "row",
    alignItems: "center",
    gap: 9,
  },
  footerLogoText: {
    color: semantic.action.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 11,
    lineHeight: 14,
    letterSpacing: 2,
  },
  footerLogoSubline: {
    color: semantic.action.primary,
    fontFamily: "Manrope_500Medium",
    fontSize: 8,
    lineHeight: 11,
    letterSpacing: 2,
  },
  powered: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 10,
    lineHeight: 14,
  },
});
